import { useEffect, useRef, useState } from "react";
import { Image } from "@chakra-ui/react";
import styled from "styled-components";

const SLIDE_DURATION_MS = 500;

// A single, persistent card slot - the parent should keep rendering the
// same CardRevealSlot instance (no remounting via `key`) and just update
// `card` as each new card is revealed. Whenever `card` changes, the new
// card slides in from the side and comes to rest on top of whatever was
// showing before (including the very first card, which slides in over
// the empty slot), rather than replacing it instantly.
const CardRevealSlot = ({ card }) => {
  const [displayCard, setDisplayCard] = useState(null);
  const [incomingCard, setIncomingCard] = useState(null);
  const [sliding, setSliding] = useState(false);
  const prevCardRef = useRef(card);

  useEffect(() => {
    if (card === prevCardRef.current) return;
    prevCardRef.current = card;
    setIncomingCard(card);

    // Defer sliding to the next two frames so the browser paints the
    // off-slot starting position first - toggling the class in the same
    // commit as the new card content would skip straight to the end
    // state and the transition would never visibly run.
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSliding(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [card]);

  const handleTransitionEnd = (e) => {
    if (e.target !== e.currentTarget) return;
    setDisplayCard(incomingCard);
    setIncomingCard(null);
    setSliding(false);
  };

  return (
    <SlotWrapper>
      <BlankFace />
      {displayCard && (
        <CardLayer>
          <Image src={displayCard.details.url} alt={displayCard.details.card} />
        </CardLayer>
      )}
      {incomingCard && (
        <CardLayer
          className={`sliding ${sliding ? "in" : ""}`}
          onTransitionEnd={handleTransitionEnd}
        >
          <Image src={incomingCard.details.url} alt={incomingCard.details.card} />
        </CardLayer>
      )}
    </SlotWrapper>
  );
};
export default CardRevealSlot;

const SlotWrapper = styled.div`
  position: relative;
  width: clamp(120px, 30vw, 180px);
  height: clamp(168px, 42vw, 252px);
  border-radius: 10px;
  overflow: hidden;
`;

const BlankFace = styled.div`
  position: absolute;
  inset: 0;
  border: 3px dashed #63aab5;
  border-radius: 10px;
  opacity: 0.35;
  box-sizing: border-box;
`;

const CardLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;

  img {
    width: 100%;
    height: 100%;
    display: block;
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &.sliding {
    z-index: 2;
    transform: translateX(100%);

    &.in {
      transform: translateX(0);
      transition: transform ${SLIDE_DURATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1);
    }
  }
`;
